import { useState } from 'react';
import {
  Box, Flex, Text, IconButton,
  Popover, PopoverTrigger, PopoverContent, PopoverBody,
  Switch, Divider, Tooltip,
} from '@chakra-ui/react';
import { LuSettings, LuTrendingUp } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import DualBalanceChart from './DualBalanceChart';
import RangeSelector from '../shared/RangeSelector';
import Figure from '../shared/Figure';
import { chartColor } from '../shared/TimeSeriesChart';
import DeltaPill from './DeltaPill';

import Card from '../shared/Card';
const ASSET_COLOR = chartColor('redesign.accent.assets');
const DEBT_COLOR = chartColor('redesign.accent.expense');

function formatNumber(n) {
  return Math.round(n || 0).toLocaleString('en-US');
}

// Up is good news for assets, bad news for debt.
function DeltaBadge({ value, accent = 'green' }) {
  const up = value >= 0;
  return <DeltaPill pct={value} up={up} good={value == null ? null : (accent === 'green' ? up : !up)} />;
}

function MetricBlock({ label, value, currency, dotColor, deltaPct, deltaAccent, approx = false, approxTooltip, size = 'card' }) {
  // The card's lead figure, its code small and raised; "≈" when converted.
  const amount = (
    <Flex align="baseline" justify="center" gap={1} cursor={approx ? 'default' : undefined}>
      {approx && <Text as="span" color="redesign.text.muted" fontSize="sm">≈</Text>}
      <Figure size={size} code={currency} amount={formatNumber(value)} />
    </Flex>
  );
  // Copilot's metric: a dotted title-case label, the figure, then its change.
  return (
    <Box textAlign="center">
      <Flex align="center" justify="center" gap="6px" mb="6px">
        <Box w="8px" h="8px" borderRadius="full" bg={dotColor} />
        <Text fontSize="14px" fontWeight={600} color="redesign.heading">{label}</Text>
      </Flex>
      {approx ? <Tooltip label={approxTooltip} hasArrow openDelay={300}>{amount}</Tooltip> : amount}
      <Box mt="8px">
        <DeltaBadge value={deltaPct} accent={deltaAccent} />
      </Box>
    </Box>
  );
}

export default function AccountsOverviewCard({
  summary,
  debtSummary,
  converted,
  convertedIsConverted = {},
  displayCurrency,
  multiCurrency = false,
  trend,
  range,
  onRangeChange,
  currency,
  chartPeriods = [],
  granularity = 'month',
  // The accounts the chart sums: the active ones, as the header totals.
  accounts = [],
  // The Dashboard's Net worth card puts its own title row on top, and its row
  // spaces the cards, so the card's own bottom margin goes.
  header = null,
}) {
  const { t } = useTranslation('accounts');
  const [combined, setCombined] = useState(false);
  // Index the cursor is over in the trend chart. The chart plots the same
  // balances these cards total, so while hovering they report that point in
  // time — otherwise the line is a shape with no readable values.
  const [hoverIdx, setHoverIdx] = useState(null);
  const [singleLine, setSingleLine] = useState(false);

  // MI-298 stock view: with a display currency active, the metric blocks show
  // every balance valued at today's rate, summed into one approximate figure.
  const converting = Boolean(displayCurrency && converted);
  const metricAssets = converting ? converted.assets : summary.assets;
  const metricDebt = converting ? converted.debt : debtSummary;
  const metricCurrency = converting ? displayCurrency : currency;

  const atHover = (values, fallback) => {
    if (hoverIdx === null || !Array.isArray(values)) return fallback;
    const v = values[hoverIdx];
    return v === undefined ? fallback : v;
  };
  // Change from the start of the visible range to the hovered point; the
  // period-over-period delta means nothing once you are reading history.
  const deltaTo = (values, fallbackPct) => {
    if (hoverIdx === null || !Array.isArray(values) || !values.length) return fallbackPct;
    const first = values[0];
    const here = values[hoverIdx];
    if (first === undefined || here === undefined || !first) return undefined;
    return ((here - first) / Math.abs(first)) * 100;
  };

  const showSingleLine = combined || singleLine;
  // Combined, the card plots net worth = assets − debt (MI-227). Its change
  // is measured against the size of the opening figure, as the hover reading
  // is, so a negative net worth that shrinks reads as a gain.
  const netTotal = metricAssets.total - (metricDebt?.total ?? 0);
  const netStart = trend.net[0];
  const netDeltaPct = netStart ? ((netTotal - netStart) / Math.abs(netStart)) * 100 : null;
  const lineColor = combined && netTotal < 0 ? DEBT_COLOR : ASSET_COLOR;

  return (
    <Card
      borderRadius="xl"
      p={6}
      mb={header ? 0 : 6}
    >
      {header && <Box mb={4}>{header}</Box>}
      {/* Metrics header — hidden in the "Todas" multi-currency view, which shows a
          pick-a-currency prompt instead (per-currency figures live in the list). */}
      {!multiCurrency && (
      <Flex justify="center" gap={16} mb={1} position="relative">
        {combined ? (
          <MetricBlock
            label={t('overview.netWorth')}
            value={atHover(trend.net, netTotal)}
            currency={metricCurrency}
            dotColor={lineColor}
            deltaPct={deltaTo(trend.net, netDeltaPct)}
            deltaAccent="green"
            approx={converting && convertedIsConverted.net}
            approxTooltip={t('common:convert.approxTodayTooltip')}
            size="md"
          />
        ) : (
          <>
            <MetricBlock
              label={t('overview.assets')}
              value={atHover(trend.assets, metricAssets.total)}
              currency={metricCurrency}
              dotColor={ASSET_COLOR}
              deltaPct={deltaTo(trend.assets, metricAssets.delta_pct)}
              deltaAccent="green"
              approx={converting && convertedIsConverted.assets}
              approxTooltip={t('common:convert.approxTodayTooltip')}
            />
            <MetricBlock
              label={t('overview.debt')}
              value={atHover(trend.expenses, metricDebt?.total ?? 0)}
              currency={metricCurrency}
              dotColor={DEBT_COLOR}
              deltaPct={deltaTo(trend.expenses, metricDebt?.delta_pct)}
              deltaAccent="red"
              approx={converting && convertedIsConverted.debt}
              approxTooltip={t('common:convert.approxTodayTooltip')}
            />
          </>
        )}

        {/* Settings gear — chart-only */}
        <Popover placement="bottom-end" isLazy>
          <PopoverTrigger>
            <IconButton
              position="absolute"
              right={0}
              top={0}
              icon={<LuSettings size={18} />}
              aria-label={t('overview.chartSettings')}
              size="sm"
              variant="ghost"
              color="redesign.accent.blue"
              _hover={{ color: 'redesign.heading', bg: 'redesign.bg.card' }}
            />
          </PopoverTrigger>
          <PopoverContent
            w="240px"
            shadow="lg"
            borderRadius="xl"
            border="1px solid"
            borderColor="redesign.border"
            _focus={{ outline: 'none' }}
          >
            <PopoverBody p={3}>
              <Flex justify="space-between" align="center" py={2}>
                <Text fontSize="sm" color="redesign.text">{t('overview.combine')}</Text>
                <Switch
                  size="sm"
                  isChecked={combined}
                  onChange={e => setCombined(e.target.checked)}
                />
              </Flex>
              <Flex justify="space-between" align="center" py={2} opacity={combined ? 0.4 : 1}>
                <Text fontSize="sm" color={combined ? 'redesign.text.muted' : 'redesign.text'}>{t('overview.singleLine')}</Text>
                <Switch
                  size="sm"
                  isChecked={singleLine}
                  isDisabled={combined}
                  onChange={e => setSingleLine(e.target.checked)}
                />
              </Flex>
              <Divider my={1} />
              <Flex justify="space-between" align="center" py={2}>
                <Text fontSize="sm" color="redesign.text">{t('overview.includedInChart')}</Text>
                <Text fontSize="sm" color="redesign.text.muted">{t('overview.accountsCount', { count: accounts.length })}</Text>
              </Flex>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Flex>
      )}

      {/* Chart — or a prompt to pick a currency when showing "Todas" */}
      {multiCurrency ? (
        <Flex direction="column" align="center" justify="center" gap={2} h="180px">
          <Box as={LuTrendingUp} boxSize="32px" color="redesign.text.faint" />
          <Text fontSize="sm" color="redesign.text.muted">{t('overview.selectCurrency')}</Text>
        </Flex>
      ) : (
        // Copilot runs the chart off the card's left edge and ends it 32px
        // short of the right one.
        <Box ml={-6} mr={2} mt={2}>
        <DualBalanceChart
          assets={combined ? trend.net : trend.assets}
          expenses={trend.expenses}
          periods={chartPeriods}
          granularity={granularity}
          singleLine={showSingleLine}
          lineColor={lineColor}
          onHover={setHoverIdx}
        />
        </Box>
      )}

      {/* Range selector — none on the Dashboard, which follows its own date filter */}
      {onRangeChange && <RangeSelector value={range} onChange={onRangeChange} />}
    </Card>
  );
}
